import { useState } from 'react';
import { PageHeader } from '../layout/PageHeader';
import { ContentWrapper } from '../layout/styled';
import { UsersTable } from './components/UsersTable';
import { Button, Tab, Tabs, useTheme } from '@mui/material';
import { OPTable } from './components/OPTable';
import { ReactComponent as AddIcon } from '../../assets/icons/add-white.svg';
import { Typography } from '../../components/Typography/Typography';
import { UserModal } from './components/UserModal/UserModal';
import { DeleteUserModal } from './components/DeleteUserModal';
import { useAtom, useAtomValue } from 'jotai';
import { isSmallScreenState } from '../../state/UIState';
import {
  selectedUserForEditAtom,
  selectedUserForDeleteAtom,
  selectedUserForResetAtom,
} from '../../state/UIAccountantState';
import { ResetUserModal } from './components/ResetUserModal';
import {
  ALL_SUBMITTERS_OPTION,
  useSubmitterLabelOptions,
} from '../../queries/hooks/useSubmitterLabelOptions';

/** "All Users" tab value; every other tab's value IS a distinct Submitter-stage override label. */
const ALL_USERS_TAB = 'all';

export const UserAdministration = () => {
  const { colors } = useTheme();
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const [selectedTab, setSelectedTab] = useState<string>(ALL_USERS_TAB);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);

  // One submitter tab per DISTINCT Submitter-stage override label across workflows (e.g. "Operating
  // Partner", "Asset Servicer"); drops the leading "All Submitters" option, which is not a tab here.
  const submitterLabelTabs = useSubmitterLabelOptions().filter(
    (option) => option.id !== ALL_SUBMITTERS_OPTION.id
  );

  const [selectedUserForEdit, setSelectedUserForEdit] = useAtom(selectedUserForEditAtom);
  const [selectedUserForDelete, setSelectedUserForDelete] = useAtom(selectedUserForDeleteAtom);
  const [selectedUserForReset, setSelectedUserForReset] = useAtom(selectedUserForResetAtom);

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const tabStyle = {
    color: colors.text.main,
    textTransform: 'none',
    fontSize: '14px',
    paddingLeft: '1rem',
    paddingRight: '1rem',
  };

  return (
    <>
      <PageHeader
        title='User Administration'
        description='Manage users, roles, and access'
        breadcrumbs={[{ title: 'Administration' }, { title: 'User Administration' }]}
        actionComponent={
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            style={{ height: '40px' }}
            onClick={() => setAddUserModalOpen(true)}
          >
            <Typography
              variant='h4'
              color={colors.textAccent.inverse}
              style={{ marginBottom: '-1px' }}
            >
              Add User
            </Typography>
          </Button>
        }
      />
      <ContentWrapper isSmallScreen={isSmallScreen}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          sx={{
            marginBottom: '1rem',
          }}
        >
          <Tab label='All Users' value={ALL_USERS_TAB} sx={tabStyle} />
          {submitterLabelTabs.map((option) => (
            <Tab key={option.id} label={option.value} value={String(option.id)} sx={tabStyle} />
          ))}
        </Tabs>
        {selectedTab === ALL_USERS_TAB ? (
          <UsersTable />
        ) : (
          <OPTable submitterLabel={selectedTab} />
        )}
      </ContentWrapper>
      {addUserModalOpen && (
        <UserModal
          userProfileOpen={addUserModalOpen}
          handleUserProfilClose={() => setAddUserModalOpen(false)}
        />
      )}
      {selectedUserForEdit && (
        <UserModal
          userProfileOpen={!!selectedUserForEdit}
          handleUserProfilClose={() => {
            setSelectedUserForEdit(null);
          }}
          user={selectedUserForEdit}
        />
      )}
      {selectedUserForDelete && (
        <DeleteUserModal
          deleteUserOpen={!!selectedUserForDelete}
          user={selectedUserForDelete}
          toggleDeleteUser={() => {
            setSelectedUserForDelete(null);
          }}
        />
      )}
      {selectedUserForReset && (
        <ResetUserModal
          resetUserOpen={!!selectedUserForReset}
          user={selectedUserForReset}
          toggleResetUser={() => {
            setSelectedUserForReset(null);
          }}
        />
      )}
    </>
  );
};
